
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReagentRequest } from "@/types/reagentRequest.types";
import { Category } from "@/types/reagent.types";
import { Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface EditReagentRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<ReagentRequest>) => Promise<void>;
  request: ReagentRequest | null;
  categories: Category[];
  isSubmitting: boolean;
}

const EditReagentRequestDialog: React.FC<EditReagentRequestDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  request,
  categories,
  isSubmitting
}) => {
  const [formData, setFormData] = useState<Partial<ReagentRequest>>({
    name: "",
    catalogue_number: "",
    category_id: "",
    description: "",
    amount: 0,
    amount_unit: "",
    number_of_packages: 0,
    storage_conditions: ""
  });

  useEffect(() => {
    if (request) {
      setFormData({
        name: request.name,
        catalogue_number: request.catalogue_number || "",
        category_id: request.category_id || "",
        description: request.description || "",
        amount: request.amount || 0,
        amount_unit: request.amount_unit || "",
        number_of_packages: request.number_of_packages || 0,
        storage_conditions: request.storage_conditions || ""
      });
    }
  }, [request]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value ? Number(value) : null }));
  };

  const handleCategoryChange = (value: string) => {
    setFormData((prev) => ({ ...prev, category_id: value === "none" ? "" : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Reagent Request</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Reagent Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="catalogue_number">Catalogue Number</Label>
              <Input
                id="catalogue_number"
                name="catalogue_number"
                value={formData.catalogue_number}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select 
                value={formData.category_id?.toString() || "none"}
                onValueChange={handleCategoryChange}
                disabled={isSubmitting}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description || ""}
                onChange={handleChange}
                disabled={isSubmitting}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  min={0}
                  step="any"
                  value={formData.amount || ""}
                  onChange={handleNumberChange}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount_unit">Unit</Label>
                <Input
                  id="amount_unit"
                  name="amount_unit"
                  value={formData.amount_unit || ""}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder="e.g., g, mg, mL"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="number_of_packages">Number of Packages</Label>
              <Input
                id="number_of_packages"
                name="number_of_packages"
                type="number"
                min={0}
                value={formData.number_of_packages || ""}
                onChange={handleNumberChange}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="storage_conditions">Storage Conditions</Label>
              <Input
                id="storage_conditions"
                name="storage_conditions"
                value={formData.storage_conditions || ""}
                onChange={handleChange}
                disabled={isSubmitting}
                placeholder="e.g., 4°C, -20°C, Room temperature"
              />
            </div>
          </form>
        </ScrollArea>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </span>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditReagentRequestDialog;
